import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ContactDialog, AUDIENCE } from "@/components/ContactDialog";
import { toast } from "sonner";
import { BookUser, Plus, Pencil, Trash2 } from "lucide-react";

const empty = { label: "", name: "", address: "", city: "", province: "", zip: "", country: "IT", phone: "", email: "", shared: false, audience: "direct_and_masters" };
const where = (r) => [r.address, [r.zip, r.city].filter(Boolean).join(" "), r.province && `(${r.province})`, r.country].filter(Boolean).join(", ");

export default function AddressBook() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/addressbook").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setLoading(true);
    try {
      const body = { ...edit }; delete body.id;
      if (edit.id) await api.put(`/addressbook/${edit.id}`, body);
      else await api.post("/addressbook", body);
      toast.success(edit.shared ? "Contatto condiviso salvato" : "Contatto salvato");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel salvataggio"); }
    finally { setLoading(false); }
  };

  const remove = async (r) => {
    if (r.shared && !window.confirm(`Eliminare "${r.label || r.name}"? Sparirà dalla rubrica di tutti i dealer.`)) return;
    try { await api.delete(`/addressbook/${r.id}`); toast.success("Contatto eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="addressbook-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><BookUser className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Contatti</span></div>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Rubrica</h1>
          <p className="text-sm text-muted-foreground mt-1">Salva mittenti e destinatari per compilare le spedizioni in un click.{isAdmin && " I contatti condivisi compaiono nella rubrica dei dealer."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <Button variant="outline" className="gap-2" onClick={() => setEdit({ ...empty, shared: true })} data-testid="add-shared-contact"><Plus className="w-4 h-4" /> Contatto condiviso</Button>}
          <Button className="gap-2" onClick={() => setEdit({ ...empty })} data-testid="add-contact"><Plus className="w-4 h-4" /> Nuovo contatto</Button>
        </div>
      </div>

      <Card className="glow-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Alias</TableHead><TableHead>Nome</TableHead><TableHead>Indirizzo</TableHead>
            <TableHead>Contatti</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`contact-row-${r.id}`}>
                <TableCell className="font-medium" data-testid={`contact-alias-${r.id}`}>{r.label || "—"}{r.shared && <Badge variant="outline" className="ml-2 text-[10px] border-primary/40 text-primary" data-testid={`contact-shared-${r.id}`}>{isAdmin ? `Condiviso · ${AUDIENCE[r.audience] || AUDIENCE.direct_and_masters}` : "Condiviso da Markoo"}</Badge>}</TableCell>
                <TableCell>{r.name || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground" data-testid={`contact-address-${r.id}`}>{where(r) || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.phone ? <PhoneDisplay value={r.phone} data-testid={`contact-phone-${r.id}`} /> : r.email || "—"}</TableCell>
                <TableCell className="text-right">
                  {r.shared && !isAdmin ? <span className="text-xs text-muted-foreground" data-testid={`contact-readonly-${r.id}`}>Pronto all'uso</span> : <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setEdit({ ...empty, ...r })} data-testid={`contact-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`contact-del-${r.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessun contatto in rubrica.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <ContactDialog edit={edit} setEdit={setEdit} onSave={save} loading={loading} />
    </div>
  );
}
